import React, { useContext } from "react";

import { ElementState, Preview } from "@creatomate/preview";

import { ElementSource, UnsplashImageResponse } from "chq";

import { Trans } from "app/utils";
import BorderRadius from "components/stories/video-studio/common/properties/BorderRadius";
import ColorFilter from "components/stories/video-studio/common/properties/ColorFilter";
import ColorOverlay from "components/stories/video-studio/common/properties/ColorOverlay";
import ElementStartEnd from "components/stories/video-studio/common/ElementStartEnd";
import Flip from "components/stories/video-studio/common/properties/Flip";
import Opacity from "components/stories/video-studio/common/properties/Opacity";
import Rotate from "components/stories/video-studio/common/properties/Rotate";
import StringValueProperty from "components/stories/video-studio/common/properties/StringValueProperty";
import TrimStart from "components/stories/video-studio/common/properties/TrimStart";
import VideoSpeed from "components/stories/video-studio/common/properties/VideoSpeed";
import Volume from "components/stories/video-studio/common/properties/Volume";

import { FITS, IMAGE_TYPE, VIDEO_TYPE } from "components/stories/video-studio/utils/constants";
import DynamicElement from "components/stories/video-studio/common/properties/template-builder/DynamicElement";
import { useTemplateBuilder } from "components/admin/templates/context/TemplateBuilderContext";
import { CreatomateContext } from "../../../contexts/VideoStudioContext";

interface MediaFieldsProps {
  activeElement: ElementState;
  preview: Preview;
}

const MediaFields: React.FC<MediaFieldsProps> = ({
  activeElement, preview }) => {
  const { data: { creatomateJson, story } } = useContext(CreatomateContext);
  const { templateBuilderSelection } = useTemplateBuilder();

  const shouldRenderElementStartEnd = () => {
    const { media } = story || {};

    if (creatomateJson?.source?.output_format === "png") {
      return false;
    }
    // if it is an image we need to be sure the story is not image type,
    // the selected media is nos unsplash and the creatomate json is not to
    // create an image

    return (
      media?.mediaType !== IMAGE_TYPE
      && !(media as unknown as File)?.type?.startsWith("image/")
      && !((media as unknown as UnsplashImageResponse)?.asset_type === "photo")
      && !((media as unknown as ElementSource)?.mediaJson?.output_format === "png")
    );
  };

  return (
    <>
      {
        templateBuilderSelection !== "none"
      && (
        <DynamicElement
          activeElement={activeElement}
          preview={preview}
          useDefaultValue={!creatomateJson}
        />
      )
      }
      {shouldRenderElementStartEnd() && (
        <ElementStartEnd
          activeElement={activeElement}
          preview={preview}
        />
      )}
      {activeElement.source.type === VIDEO_TYPE && (
        <>
          <Volume activeElement={activeElement} preview={preview} />
          <TrimStart activeElement={activeElement} preview={preview} />
          <VideoSpeed activeElement={activeElement} preview={preview} />
        </>
      )}
      <StringValueProperty
        activeElement={activeElement}
        defaultValue="cover"
        name="fit"
        options={FITS}
        required
        preview={preview}
        propertyName="fit"
        useDefaultValue={!creatomateJson}
      >
        <Trans>Fit</Trans>
      </StringValueProperty>

      <div className="Options-radius-opacity">
        <BorderRadius activeElement={activeElement} preview={preview} />
        <Opacity activeElement={activeElement} preview={preview} />
      </div>
      <div className="AlignmentOptions">
        <Flip activeElement={activeElement} preview={preview} />
        <Rotate activeElement={activeElement} preview={preview} />
      </div>
      <ColorOverlay activeElement={activeElement} preview={preview} />
      <ColorFilter
        activeElement={activeElement}
        preview={preview}
        useDefaultValue={!creatomateJson}
      />
    </>
  );
};

export default MediaFields;
